/**
 * About popup - opened by clicking the brand mark "A" in the top bar (Item 10d).
 *
 * Displays: app name (i18n), version, tagline.
 * Styled to match settings-popup.
 */

import { t } from '../i18n';
import { openManualPopup } from './manual-popup';

let closeAbout: (() => void) | null = null;

/** Open (or close if already open) the About popup anchored near the given trigger. */
export function openAboutPopup(anchor: HTMLElement): void {
  // Toggle: close if already open.
  if (closeAbout !== null) {
    closeAbout();
    return;
  }

  const popup = document.createElement('div');
  popup.className = 'settings-popup about-popup';
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-modal', 'false');

  const name = document.createElement('div');
  name.className = 'about-popup-name';
  name.textContent = t('app.name');

  const version = document.createElement('div');
  version.className = 'settings-popup-label';
  // __APP_VERSION__ is injected by vite.config.ts define; falls back to '?' in test env.
  version.textContent = `v${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?'}`;

  const tagline = document.createElement('div');
  tagline.className = 'about-popup-tagline';
  tagline.textContent = t('app.tagline');

  const manualButton = document.createElement('button');
  manualButton.type = 'button';
  manualButton.className = 'btn about-manual-btn';
  manualButton.dataset['i18n'] = 'about.manual';
  manualButton.textContent = t('about.manual');
  manualButton.addEventListener('click', () => {
    openManualPopup(manualButton);
  });

  popup.appendChild(name);
  popup.appendChild(version);
  popup.appendChild(tagline);
  popup.appendChild(manualButton);
  document.body.appendChild(popup);

  // Position below the anchor element.
  const rect = anchor.getBoundingClientRect();
  popup.style.cssText = [
    'position:fixed',
    `left:${rect.left}px`,
    `top:${rect.bottom + 4}px`,
    'z-index:9000',
  ].join(';');

  // Clamp to viewport.
  const pr = popup.getBoundingClientRect();
  if (pr.right > window.innerWidth) {
    popup.style.left = `${window.innerWidth - pr.width - 8}px`;
  }

  function close(): void {
    if (closeAbout !== close) return;
    closeAbout = null;
    document.removeEventListener('click', handleOutside, true);
    document.removeEventListener('keydown', handleKey, true);
    popup.remove();
  }

  function handleOutside(event: MouseEvent): void {
    if (!popup.contains(event.target as Node) && event.target !== anchor) {
      close();
    }
  }

  function handleKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  }

  setTimeout(() => {
    document.addEventListener('click', handleOutside, true);
    document.addEventListener('keydown', handleKey, true);
  }, 0);

  closeAbout = close;
}
